import { render, waitFor, screen, fireEvent } from '@testing-library/preact';
import userEvent from '@testing-library/user-event';
import { expect } from 'chai';

import translations from '#resources/nls/en/bundle';
import { getClientHints } from '#utils/PRIVATE_clientHints';
import {
  getRoot,
  getItems,
  populateData,
  TestComponentsProps,
  mouseItemSelection,
  fireDragAndDropEvent,
  keyboardReorder
} from './testHelpers';

export const testRender = ({
  GenericCardGrid,
  ActionCardGrid,
  SelectionCardGrid,
  ContentUpdatableCardGrid
}: TestComponentsProps<string | number>) => {
  it('initial render', async function () {
    const data = populateData(100);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect(items.length).eq(25);
  });

  it('checking key type - string key', async () => {
    const data = populateData(3);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect((items[0] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[1] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
    expect((items[2] as HTMLElement).dataset['ojKeyType'] === undefined).eq(true);
  });

  it('checking key type - numeric key', async () => {
    const data = populateData(3, true);
    render(<GenericCardGrid data={data}></GenericCardGrid>);
    const items = await getItems('gridcell');
    expect((items[0] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[1] as HTMLElement).dataset['ojKeyType']).eq('number');
    expect((items[2] as HTMLElement).dataset['ojKeyType']).eq('number');
  });

  it('aria attributes - multiselectable and selected, selectionMode is none', async () => {
    const data = populateData(2);
    render(<SelectionCardGrid data={data}></SelectionCardGrid>);
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq(null);
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq(null);
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq(null);
  });

  it('aria attributes - multiselectable and selected, selectionMode is single', async () => {
    const data = populateData(2);
    render(
      <SelectionCardGrid
        data={data}
        selectionMode={'single'}
        selectedKeys={new Set(['1'])}></SelectionCardGrid>
    );
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq('false');
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq('false');
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq('true');
  });

  it('aria attributes - multiselectable and selected, selectionMode is multiple', async () => {
    const data = populateData(3);
    render(
      <SelectionCardGrid
        data={data}
        selectionMode={'multiple'}
        selectedKeys={new Set(['1', '2'])}></SelectionCardGrid>
    );
    const items = await getItems('gridcell');
    const grid = await getRoot();

    expect((grid as HTMLElement).getAttribute('aria-multiselectable')).eq('true');
    expect((items[0] as HTMLElement).getAttribute('aria-selected')).eq('false');
    expect((items[1] as HTMLElement).getAttribute('aria-selected')).eq('true');
    expect((items[2] as HTMLElement).getAttribute('aria-selected')).eq('true');
  });

  // skip this test in jest, only run it in karma mocha
  if (typeof jest === typeof undefined) {
    // because jest doesn't show grid layout, the number of focusable elements is not calculated correctly
    it('content focusBehavior - valid case', async () => {
      const data = populateData(3);
      render(<ActionCardGrid data={data}></ActionCardGrid>);
      const items = await getItems('gridcell');

      await userEvent.tab();

      await waitFor(() => {
        expect(items[0].matches(':focus-within')).to.be.true;
      });
    });

    it('content focusBehavior - invalid case', async () => {
      const data = populateData(3);
      render(<ActionCardGrid data={data} showButton={true}></ActionCardGrid>);
      const grid = await getRoot();

      await userEvent.tab();
      await waitFor(() => {
        expect(grid.matches(':focus')).to.be.true;
      });
    });
  }

  // JET-61732
  it('memoization - update memoized children', async () => {
    const data = populateData(3);
    const { queryByTestId } = render(
      <ContentUpdatableCardGrid data={data}></ContentUpdatableCardGrid>
    );

    const elem0 = queryByTestId(data[0].id) as HTMLElement;
    const elem1 = queryByTestId(data[1].id) as HTMLElement;
    const elem2 = queryByTestId(data[2].id) as HTMLElement;
    expect(elem0?.innerHTML, 'the 1st item initial count should be 0').eq('0');
    expect(elem1?.innerHTML, 'the 2nd item initial count should be 0').eq('0');
    expect(elem2?.innerHTML, 'the 3rd item initial count should be 0').eq('0');

    const button = queryByTestId('mybutton') as HTMLElement;
    await userEvent.click(button);

    expect(elem0?.innerHTML, 'the 1st item new count should be 1').eq('1');
    expect(elem1?.innerHTML, 'the 2nd item new count should be 1').eq('1');
    expect(elem2?.innerHTML, 'the 3rd item new count should be 1').eq('1');
  });

  it('reorder - fire Drag and Drop Event', async () => {
    const data = populateData(5);
    const { queryByTestId } = render(<GenericCardGrid data={data} isReorderEnabled={true} />);

    let items = await getItems('gridcell');
    const count = items.length;
    const firstItemDragIcon = queryByTestId('drag-icon-0') as HTMLElement;
    const thirdItemDragIcon = queryByTestId('drag-icon-2') as HTMLElement;
    const firstItem = items[0];

    // drag the first item and drop on the third item
    fireDragAndDropEvent(firstItemDragIcon, thirdItemDragIcon);
    await waitFor(async () => {
      items = await getItems('gridcell');
      const updatedThirdItem = items[2];
      expect(firstItem).equals(updatedThirdItem);
      expect(items.length).equal(count);
    });
  });

  it('reorder - reorder the first item to right using keyboard', async () => {
    const data = populateData(5);
    render(<GenericCardGrid data={data} isReorderEnabled={true} />);

    const grid = await getRoot();
    let items = await getItems('gridcell');
    const count = items.length;
    const firstItem = items[0];

    // reorder the first item to left
    mouseItemSelection(firstItem);
    keyboardReorder(grid, 'ArrowLeft');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[0];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // reorder the first item to right
    keyboardReorder(grid, 'ArrowRight');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[1];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // continuous reorder the first item to right
    keyboardReorder(grid, 'ArrowRight');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[2];
      expect(firstItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });
  });

  it('reorder - reorder the last item to left using keyboard', async () => {
    const data = populateData(5);
    render(<GenericCardGrid data={data} isReorderEnabled={true} />);

    const grid = await getRoot();
    let items = await getItems('gridcell');
    const count = items.length;
    const lastItem = items[items.length - 1];

    // reorder the last item to right
    mouseItemSelection(lastItem);
    keyboardReorder(grid, 'ArrowRight');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[items.length - 1];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // reorder the last item to left
    keyboardReorder(grid, 'ArrowLeft');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[items.length - 2];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });

    // continuous reorder the last item to left
    keyboardReorder(grid, 'ArrowLeft');
    await waitFor(async () => {
      items = await getItems('gridcell');
      const reorderedItem = items[items.length - 3];
      expect(lastItem).equals(reorderedItem);
      expect(items.length).equal(count);
    });
  });

  if (typeof jest == typeof undefined) {
    it('reorder - instruction text should exist when reorder is enabled', async () => {
      // get instruction text
      const { deviceType, platform } = getClientHints();
      const keyPressed =
        platform === 'windows' ? translations.accControlKey() : translations.accCommandKey();
      const reorderInstructions =
        deviceType === 'phone' || deviceType === 'tablet'
          ? ''
          : translations.accReorderInstructions({
              keyPressed: keyPressed
            });

      // instruction text should exist when reorder is enabled
      const data = populateData(3);
      const { rerender } = render(<GenericCardGrid data={data} isReorderEnabled={true} />);
      let instructionText = screen.queryByText(reorderInstructions);
      expect(instructionText).not.to.be.null;

      // instruction text should not exist when reorder is disabled
      rerender(<GenericCardGrid data={data} />);
      instructionText = screen.queryByText(reorderInstructions);
      expect(instructionText).to.be.null;
    });
  }

  // test in karma mocha only since it involves scrolling
  if (typeof jest === typeof undefined) {
    it('scroll into view - when cardview regains focus', async () => {
      const data = populateData(10);
      render(<GenericCardGrid data={data} initialCurrentKey={data[9].id} />);

      // the initial current item is Employee 9, so the scrollTop is high
      const grid = await getRoot();
      await waitFor(() => {
        expect(grid.scrollTop).to.greaterThanOrEqual(450);
      });

      // scroll cardview up until scrollTop is 0
      grid.scrollTop = 0;
      await waitFor(() => {
        expect(grid.scrollTop).to.eq(0);
      });

      // tab into cardview, should scroll Employee 9 into view
      grid.focus();
      await waitFor(() => {
        expect(grid.scrollTop).to.greaterThanOrEqual(450);
      });
    });
  }

  // test in karma mocha only since it involves rerender
  if (typeof jest === typeof undefined) {
    it('load more on scroll - should fetch data once during keyboard navigation', async () => {
      const data = populateData(21);
      render(<GenericCardGrid data={data} fetchSize={7} />);

      let items = await getItems('gridcell');
      const thirdItem = items[3];
      await userEvent.click(thirdItem);

      const grid = await getRoot();
      expect(grid.matches(':focus')).to.be.true;
      fireEvent.keyDown(grid, { key: 'ArrowDown' });

      await waitFor(async () => {
        items = await getItems('gridcell');
        expect(items.length).to.eq(14);
      });

      // load more should only happen once
      try {
        await waitFor(async () => {
          items = await getItems('gridcell');
          expect(items.length).to.eq(21);
        });
      } catch (e) {
        expect(e).not.null;
      }
    }, 6000);
  }
};
